import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import { createRef, forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styles from './drop-down.module.scss';
export const DropDown = /*#__PURE__*/ forwardRef(({ children, onClose, onKeyDown, onOptionSelect, options, renderAs = 'div', variant = 'base', ...props }, ref)=>{
    const [isOpen, setIsOpen] = useState(false);
    const optionsRef = useRef(null);
    const optionRefs = useMemo(()=>options.map(()=>/*#__PURE__*/ createRef()), [
        options
    ]);
    const Tag = renderAs;
    const toggleIsOpen = useCallback((isOpen)=>{
        setIsOpen(isOpen);
        if (!isOpen) {
            onClose?.();
        }
    }, [
        onClose
    ]);
    useImperativeHandle(ref, ()=>({
            close: ()=>toggleIsOpen(false),
            open: ()=>toggleIsOpen(true)
        }), [
        toggleIsOpen
    ]);
    const focusOption = (index)=>{
        const optionElement = optionRefs[index]?.current;
        if (optionElement) {
            optionElement?.focus();
            optionsRef.current?.scrollTo({
                behavior: 'smooth',
                top: optionElement.offsetTop - (optionsRef.current.getBoundingClientRect().height - optionElement.getBoundingClientRect().height) / 2
            });
        }
        toggleIsOpen(index >= 0);
    };
    const clearInput = ()=>{
        focusOption(-1);
        onOptionSelect('');
    };
    const handleKey = (event, actions)=>{
        const action = actions[event.key];
        if (action) {
            event.preventDefault();
            event.stopPropagation();
            action();
        }
        onKeyDown?.(event);
    };
    const handleOuterKeyDown = (event)=>{
        const moveDown = ()=>focusOption(0);
        const moveUp = ()=>focusOption(options.length - 1);
        handleKey(event, {
            ArrowUp: moveUp,
            ArrowDown: moveDown,
            Escape: clearInput
        });
    };
    const handleInnerKeyDown = (index, event)=>{
        const moveDown = ()=>focusOption((index + 1) % options.length);
        const moveUp = ()=>focusOption(index - 1);
        const selectCurrent = ()=>onOptionSelect(options[index].value);
        const closeDropdown = ()=>toggleIsOpen(false);
        handleKey(event, {
            ArrowUp: moveUp,
            ArrowDown: moveDown,
            Enter: selectCurrent,
            ' ': selectCurrent,
            Tab: closeDropdown
        });
    };
    return /*#__PURE__*/ _jsxs(Tag, {
        ...props,
        className: classNames('drop-down', styles[`variant-${variant}-root`], props.className),
        "data-open": isOpen || undefined,
        onKeyDown: handleOuterKeyDown,
        children: [
            children,
            /*#__PURE__*/ _jsx("div", {
                className: styles[`variant-${variant}-container`],
                children: /*#__PURE__*/ _jsx("div", {
                    className: styles[`variant-${variant}-content`],
                    children: /*#__PURE__*/ _jsxs("ul", {
                        className: styles[`variant-${variant}-options`],
                        "data-testid": "options",
                        ref: optionsRef,
                        children: [
                            options.length === 0 && /*#__PURE__*/ _jsx("li", {
                                className: styles[`variant-${variant}-no-result`],
                                children: "Dazu wurde leider kein Eintrag gefunden."
                            }),
                            options.map(({ label, value }, index)=>/*#__PURE__*/ _jsx("li", {
                                    "aria-selected": false,
                                    className: styles[`variant-${variant}-option`],
                                    onClick: ()=>onOptionSelect(value),
                                    onKeyDown: (event)=>handleInnerKeyDown(index, event),
                                    ref: optionRefs[index],
                                    // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: TODO
                                    role: "option",
                                    tabIndex: -1,
                                    children: label ?? value
                                }, label + value))
                        ]
                    })
                })
            })
        ]
    });
});

//# sourceMappingURL=drop-down.js.map